import { z } from "zod";

/**
 * Shared DTO cho Admin Fund Directory & Detail (US-AE-IMP2-T10 · @UC #7c admin-scope fund).
 * READ-ONLY: list + detail admin-scope (quỹ toàn hệ, KHÔNG nested trong group).
 * Một nguồn sự thật cho api (query + redaction theo vai) và admin web (render), tránh drift shape.
 *
 * BẢO MẬT (invariants):
 * - B1: tiền = BigInt minor units serialize thành STRING, không float.
 * - Field redact-được (balanceMinor) = nullable — vai thấp (SUPPORT/READ_ONLY) không xem số dư.
 * - `.strict()` — KHÔNG khai báo field lạ; field lạ bị reject tại biên (chống vô tình leak).
 * - Cursor opaque string — đối xứng common/pagination/cursor.
 *
 * Envelope khớp quy ước list repo: `{ data, pageInfo: { hasNext, nextCursor } }`.
 *
 * Limit: default 20 (backend áp khi vắng), max 50. q OPTIONAL (tên quỹ). groupId OPTIONAL (lọc theo nhóm).
 */

/** Max page-size. Default (20) do backend áp khi vắng. */
export const ADMIN_FUND_DIRECTORY_MAX_LIMIT = 50;

/** Độ dài tối đa của `q` — chặn input phình to tại biên. 256 dư cho tên quỹ/uuid. */
export const ADMIN_FUND_DIRECTORY_MAX_QUERY_LENGTH = 256;

/**
 * Query list: `q` OPTIONAL (tên quỹ, ≤256), `groupId` OPTIONAL (uuid — lọc theo nhóm),
 * cursor opaque optional, limit ∈ [1,50] optional. Vắng ⇒ liệt kê toàn bộ (cursor).
 */
export const adminFundDirectoryQuerySchema = z.object({
  q: z.string().max(ADMIN_FUND_DIRECTORY_MAX_QUERY_LENGTH).optional(),
  groupId: z.string().uuid().optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(ADMIN_FUND_DIRECTORY_MAX_LIMIT).optional(),
});

export type AdminFundDirectoryQuery = z.infer<typeof adminFundDirectoryQuerySchema>;

/** Trạng thái hiển thị vòng đời quỹ — SUY RA từ isArchived (Fund KHÔNG có cột status). */
export const adminFundStatusSchema = z.enum(["active", "archived"]);
export type AdminFundStatus = z.infer<typeof adminFundStatusSchema>;

/**
 * List item (AC1). balanceMinor = STRING (BigInt minor) hoặc NULL (redact theo vai) — KHÔNG float (B1).
 * groupName NULLABLE (nhóm đã xoá mềm / thiếu tên). KHÔNG bao giờ chứa secret (`.strict()`).
 */
export const adminFundDirectoryItemSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    groupId: z.string(),
    groupName: z.string().nullable(),
    currency: z.string(),
    /** BigInt minor units as string; null when redacted for lower-privilege roles. KHÔNG number. */
    balanceMinor: z.string().nullable(),
    status: adminFundStatusSchema,
    createdAt: z.string(),
  })
  .strict();

export type AdminFundDirectoryItem = z.infer<typeof adminFundDirectoryItemSchema>;

/** Envelope khớp quy ước repo `{ data, pageInfo }` (cursor opaque, hasNext). */
export const adminFundDirectoryResponseSchema = z.object({
  data: z.array(adminFundDirectoryItemSchema),
  pageInfo: z.object({
    hasNext: z.boolean(),
    nextCursor: z.string().nullable(),
  }),
});

export type AdminFundDirectoryResponse = z.infer<typeof adminFundDirectoryResponseSchema>;

/**
 * Fund detail admin-scope (AC2). Link tới groupId (FE điều hướng /groups/:groupId).
 * balanceMinor = STRING (BigInt) hoặc NULL (redact theo vai). `type` NULLABLE (quỹ cũ). KHÔNG secret.
 */
export const adminFundDetailSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    groupId: z.string(),
    groupName: z.string().nullable(),
    currency: z.string(),
    type: z.string().nullable(),
    balanceMinor: z.string().nullable(),
    status: adminFundStatusSchema,
    createdAt: z.string(),
  })
  .strict();

export type AdminFundDetail = z.infer<typeof adminFundDetailSchema>;
